import * as z from 'zod';
import { CouponTypes } from './coupon.data';

/* Helpers */
const numberFromInput = opts => {
  let schema = z.preprocess(
    val => (val === '' || val === undefined ? undefined : Number(val)),
    z.number({
      required_error: 'Required',
      invalid_type_error: 'Must be a number',
    })
  );

  if (!opts?.required) {
    schema = schema.optional();
  }

  if (opts?.positive) {
    schema = schema.refine(val => val === undefined || val > 0, {
      message: 'Must be greater than 0',
    });
  }

  return schema;
};

const dateFromInput = z.preprocess(
  val => (val ? new Date(val) : undefined),
  z.date({
    required_error: 'Required',
    invalid_type_error: 'Invalid date',
  })
);

/* Common Text Fields */
const textFields = {
  title_lt: z.string().min(1, 'Title (LT) is required'),
  title_en: z.string().min(1, 'Title (EN) is required'),
  title_ru: z.string().min(1, 'Title (RU) is required'),

  description_lt: z.string().optional(),
  description_en: z.string().optional(),
  description_ru: z.string().optional(),
};

/* Usage Type Union */
const singleUseSchema = z.object({
  usage_type: z.literal(1),
  max_redeem_quantity: numberFromInput({ required: false }),
});

const multiUseSchema = z.object({
  usage_type: z.literal(2),
  max_redeem_quantity: numberFromInput({
    required: true,
    positive: true,
  }),
});

const usageSchema = z.discriminatedUnion('usage_type', [
  singleUseSchema,
  multiUseSchema,
]);

/* Unique Code Union */
const autoCodeSchema = z.object({
  nominal_of_code: z.literal(false),
  code: z.string().optional(),
});

const manualCodeSchema = z.object({
  nominal_of_code: z.literal(true),
  code: z
    .string({
      required_error: 'Custom code is required',
    })
    .min(1, 'Custom code is required'),
});

const codeSchema = z.discriminatedUnion('nominal_of_code', [
  autoCodeSchema,
  manualCodeSchema,
]);

/* Base Shared Fields */
const baseFields = z
  .object({
    code_amount: numberFromInput({
      required: true,
      positive: true,
    }),

    valid_from: dateFromInput,
    valid_until: dateFromInput,

    ...textFields,
  })
  .refine(data => data.valid_until > data.valid_from, {
    message: '"Valid until" must be later than "Valid from"',
    path: ['valid_until'],
  });

/* Coupon Type Variants */
const virtualSchema = z.object({
  type: z.literal(CouponTypes.VIRTUAL),
  amount: numberFromInput({
    required: true,
    positive: true,
  }),
});

const discountAmountSchema = numberFromInput({ required: true }).refine(
  val => val !== undefined && val >= 1 && val <= 100,
  {
    message: 'Discount must be between 1 and 100%',
  }
);

const singleOrderDiscountSchema = z.object({
  type: z.literal(CouponTypes.SINGLE_ORDER_DISCOUNT),
  amount: discountAmountSchema,
});

const periodDiscountSchema = z.object({
  type: z.literal(CouponTypes.PERIOD_DISCOUNT),
  amount: discountAmountSchema,
});

const membershipSchema = z.object({
  type: z.literal(CouponTypes.MEMBERSHIP),
  amount: numberFromInput({
    required: false,
    positive: true,
  }),
  membership_id: numberFromInput({
    required: true,
    positive: true,
  }),
  discount_valid_days: numberFromInput({
    required: true,
    positive: true,
  }),
});

/* Combine Everything */
const typeSchema = z.discriminatedUnion('type', [
  virtualSchema,
  singleOrderDiscountSchema,
  periodDiscountSchema,
  membershipSchema,
]);

export const couponSchema = typeSchema
  .and(usageSchema)
  .and(codeSchema)
  .and(baseFields);
